import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, useProject } from "../App";
import { 
  Users, CheckSquare, Calendar, TrendingUp, ArrowUpRight, ArrowDownRight,
  Flame, ThermometerSun, Snowflake, Clock, Building2, Phone, MessageSquare,
  AlertTriangle, ShieldAlert, Info, RefreshCw, UserPlus, ChevronLeft, ChevronRight,
  Search, X, Globe, Home, DoorOpen, PhoneIncoming, MessageCircle, Facebook,
  Instagram, Linkedin, Handshake, Newspaper, Megaphone, Mail, Heart, Circle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import Layout from "../components/Layout";
import { API } from "../App";
import { DateRangePicker, openLeadsTab } from "../components/DateRangePicker";
import { InfoTip } from "../components/InfoTip";
import { appendDateParams } from "../utils/dateFilters";
import { last30Days } from "../lib/dateDefaults";
import { RecommendedTodos } from "../components/RecommendedTodos";

const _DEF = last30Days();
import { ExternalLeadInboxCard } from "../components/ExternalLeadInboxCard";
import { SOURCES } from "../constants/dropdownOptions";

// Source → icon + brand color map (for Lead Sources widget)
const SOURCE_META = {
  website:         { Icon: Globe,          iconCls: "text-slate-600",  barCls: "bg-slate-500" },
  "99acres":       { Icon: Building2,      iconCls: "text-indigo-700", barCls: "bg-indigo-700" },
  magicbricks:     { Icon: Building2,      iconCls: "text-red-600",    barCls: "bg-red-600" },
  housing:         { Icon: Home,           iconCls: "text-teal-600",   barCls: "bg-teal-600" },
  referral:        { Icon: Users,          iconCls: "text-blue-600",   barCls: "bg-blue-600" },
  walk_in:         { Icon: DoorOpen,       iconCls: "text-purple-600", barCls: "bg-purple-600" },
  ivr_incoming:    { Icon: PhoneIncoming,  iconCls: "text-slate-600",  barCls: "bg-slate-600" },
  whatsapp:        { Icon: MessageCircle,  iconCls: "text-green-600",  barCls: "bg-green-600" },
  facebook:        { Icon: Facebook,       iconCls: "text-blue-700",   barCls: "bg-blue-700" },
  google:          { Icon: Search,         iconCls: "text-amber-600",  barCls: "bg-amber-600" },
  instagram:       { Icon: Instagram,      iconCls: "text-pink-600",   barCls: "bg-pink-600" },
  linkedin:        { Icon: Linkedin,       iconCls: "text-sky-700",    barCls: "bg-sky-700" },
  channel_partner: { Icon: Handshake,      iconCls: "text-violet-600", barCls: "bg-violet-600" },
  newspaper:       { Icon: Newspaper,      iconCls: "text-slate-700",  barCls: "bg-slate-700" },
  hoarding:        { Icon: Megaphone,      iconCls: "text-amber-700",  barCls: "bg-amber-700" },
  sms:             { Icon: MessageSquare,  iconCls: "text-emerald-600",barCls: "bg-emerald-600" },
  events:          { Icon: Calendar,       iconCls: "text-orange-600", barCls: "bg-orange-600" },
  cold_call:       { Icon: Phone,          iconCls: "text-slate-500",  barCls: "bg-slate-500" },
  email:           { Icon: Mail,           iconCls: "text-indigo-500", barCls: "bg-indigo-500" },
  mhm_lite:        { Icon: Heart,          iconCls: "text-amber-600",  barCls: "bg-amber-500" },
  other:           { Icon: Circle,         iconCls: "text-slate-400",  barCls: "bg-slate-400" },
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedProjectId } = useProject();
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [anomalies, setAnomalies] = useState(null);
  const [anomalyLoading, setAnomalyLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [dateFrom, setDateFrom] = useState(_DEF.from);
  const [dateTo, setDateTo] = useState(_DEF.to);
  const [conversionFlipped, setConversionFlipped] = useState(false);
  const [currentDropoffIndex, setCurrentDropoffIndex] = useState(0);
  const flipIntervalRef = useRef(null);

  // Master search
  const [masterSearch, setMasterSearch] = useState("");
  const [masterResults, setMasterResults] = useState([]);
  const [masterSearching, setMasterSearching] = useState(false);
  const [showMasterDropdown, setShowMasterDropdown] = useState(false);
  const masterSearchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (masterSearchRef.current && !masterSearchRef.current.contains(e.target)) {
        setShowMasterDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!masterSearch || masterSearch.length < 2) {
      setMasterResults([]);
      setShowMasterDropdown(false);
      return;
    }
    const timer = setTimeout(async () => {
      setMasterSearching(true);
      setShowMasterDropdown(true);
      try {
        const res = await axios.get(`${API}/leads`, { params: { search: masterSearch, limit: 8 } });
        setMasterResults(res.data.leads || []);
      } catch {
        setMasterResults([]);
      } finally {
        setMasterSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [masterSearch]);

  useEffect(() => {
    fetchDashboardData();
  }, [dateFrom, dateTo, selectedProjectId]);

  useEffect(() => {
    if (user?.role === "admin") {
      axios.get(`${API}/governance/users/pending`).then(res => {
        setPendingUsers(Array.isArray(res.data) ? res.data : []);
      }).catch(() => {});
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const dateParams = new URLSearchParams();
      appendDateParams(dateParams, dateFrom, dateTo);
      dateParams.set("source_basis", "latest");
      if (selectedProjectId !== "all") dateParams.set("project_id", selectedProjectId);
      const qs = dateParams.toString();
      const [statsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard${qs ? `?${qs}` : ""}`),
        axios.get(`${API}/leads?limit=5${qs ? `&${qs}` : ""}`)
      ]);
      setStats(statsRes.data);
      setRecentLeads(leadsRes.data.leads);
    } catch (error) {
      console.error("Dashboard fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnomalies = async (forceRefresh = false) => {
    setAnomalyLoading(true);
    try {
      const res = await axios.get(`${API}/ai/anomaly-detection${forceRefresh ? '?force_refresh=true' : ''}`);
      setAnomalies(res.data);
    } catch (error) {
      console.error("Anomaly fetch error:", error);
    } finally {
      setAnomalyLoading(false);
    }
  };

  useEffect(() => {
    fetchAnomalies();
  }, []);

  // Auto-flip conversion widget: Conversion Rate → Stage 1 → Stage 2 → ... → back to Conversion
  useEffect(() => {
    const dropoffStages = (stats?.stage_dropoff || []).filter(s => s.stage !== 'booked');
    const totalSlides = 1 + dropoffStages.length; // 1 for conversion + each stage
    
    flipIntervalRef.current = setInterval(() => {
      setCurrentDropoffIndex(prev => {
        const next = (prev + 1) % totalSlides;
        setConversionFlipped(next > 0); // 0 = conversion rate, 1+ = dropoff stages
        return next;
      });
    }, 4000);
    return () => clearInterval(flipIntervalRef.current);
  }, [stats?.stage_dropoff]);

  const formatINR = (amount) => {
    if (!amount) return "N/A";
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const getScoreBadge = (score) => {
    const styles = {
      hot: "bg-red-100 text-red-700",
      warm: "bg-amber-100 text-amber-700",
      nurture: "bg-blue-100 text-blue-700",
      dormant: "bg-slate-100 text-slate-500",
      new: "bg-emerald-100 text-emerald-700"
    };
    return styles[score] || styles.new;
  };

  const getStageBadge = (stage) => {
    const styles = {
      new: "bg-emerald-50 text-emerald-700",
      contacted: "bg-blue-50 text-blue-700",
      qualified: "bg-indigo-50 text-indigo-700",
      site_visit: "bg-purple-50 text-purple-700",
      negotiation: "bg-amber-50 text-amber-700",
      booked: "bg-green-50 text-green-700",
      lost: "bg-red-50 text-red-700"
    };
    return styles[stage] || styles.new;
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
                <div className="h-4 bg-slate-200 rounded w-1/2 mb-4"></div>
                <div className="h-8 bg-slate-200 rounded w-3/4"></div>
              </div>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Manrope']">Dashboard</h1>
            <p className="text-slate-500 mt-1">Welcome back! Here's your sales overview.</p>
          </div>
          <div className="flex items-center gap-3">
            <DateRangePicker
              dateFrom={dateFrom}
              dateTo={dateTo}
              onChange={(f, t) => { setDateFrom(f); setDateTo(t); }}
            />
            <Button 
              data-testid="new-lead-btn"
              onClick={() => navigate('/leads?new=true')}
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              + New Lead
            </Button>
          </div>
        </div>

        {/* Master Search */}
        <div className="relative max-w-2xl" ref={masterSearchRef}>
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search leads by name, phone, email, or ID..."
              value={masterSearch}
              onChange={(e) => setMasterSearch(e.target.value)}
              onFocus={() => { if (masterResults.length > 0) setShowMasterDropdown(true); }}
              className="pl-11 h-12 text-base bg-white border-slate-200 shadow-sm focus:ring-2 focus:ring-slate-900/10"
              data-testid="master-search-input"
            />
            {masterSearch && (
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => { setMasterSearch(""); setMasterResults([]); setShowMasterDropdown(false); }}
                data-testid="master-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {showMasterDropdown && (masterResults.length > 0 || masterSearching) && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-80 overflow-y-auto" data-testid="master-search-dropdown">
              {masterSearching ? (
                <div className="p-4 text-center text-slate-400 text-sm">Searching...</div>
              ) : (
                masterResults.map(lead => (
                  <div
                    key={lead.lead_id}
                    className="px-4 py-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0 transition-colors"
                    onClick={() => {
                      navigate(`/leads/${lead.lead_id}`);
                      setMasterSearch("");
                      setMasterResults([]);
                      setShowMasterDropdown(false);
                    }}
                    data-testid={`master-search-result-${lead.lead_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-slate-900">{lead.name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {lead.phone}{lead.email ? ` · ${lead.email}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">{lead.stage?.replace(/_/g, " ")}</Badge>
                        {lead.project_interest && (
                          <span className="text-xs text-slate-400">{lead.project_interest}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
              {!masterSearching && masterResults.length === 0 && masterSearch.length >= 2 && (
                <div className="p-4 text-center text-slate-400 text-sm">No leads found</div>
              )}
            </div>
          )}
        </div>

        {/* Pending Users Alert */}
        {pendingUsers.length > 0 && user?.role === "admin" && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between" data-testid="pending-users-banner">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-semibold text-amber-900">{pendingUsers.length} team member{pendingUsers.length > 1 ? "s" : ""} waiting for approval</p>
                <p className="text-sm text-amber-700">{pendingUsers.map(u => u.name).join(", ")}</p>
              </div>
            </div>
            <Button 
              size="sm" 
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => navigate("/governance")}
              data-testid="go-to-governance-btn"
            >
              Review & Approve
            </Button>
          </div>
        )}

        {/* Recommended To-Do List */}
        <RecommendedTodos />

        {/* External Lead Inbox (webhook-captured leads awaiting first contact) */}
        <ExternalLeadInboxCard compact={true} maxRows={5} />

        {/* KPI Cards - Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" data-testid="total-leads-card" onClick={() => openLeadsTab({ date_from: dateFrom, date_to: dateTo })}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Total Leads</p>
                  <p className="text-3xl font-bold text-slate-900 font-['Manrope'] mt-1">{stats?.total_leads || 0}</p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-slate-600" />
                </div>
              </div>
              <div className="flex items-center gap-1 mt-3 text-sm">
                <ArrowUpRight className="w-4 h-4 text-emerald-500" />
                <span className="text-emerald-500 font-medium">12%</span>
                <span className="text-slate-500">vs last month</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" data-testid="hot-leads-card" onClick={() => openLeadsTab({ score: "hot", date_from: dateFrom, date_to: dateTo })}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Hot Leads</p>
                  <p className="text-3xl font-bold text-red-600 font-['Manrope'] mt-1">{stats?.hot_leads || 0}</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <Flame className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <p className="text-sm text-slate-500 mt-3">Require immediate attention</p>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid="pending-tasks-card" onClick={() => navigate("/tasks")}>
            <CardContent className="p-6 cursor-pointer">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Pending Tasks</p>
                  <p className="text-3xl font-bold text-amber-600 font-['Manrope'] mt-1">{stats?.pending_tasks || 0}</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <CheckSquare className="w-6 h-6 text-amber-600" />
                </div>
              </div>
              {stats?.overdue_tasks > 0 && (
                <p className="text-sm text-red-500 mt-3 font-medium">{stats.overdue_tasks} overdue</p>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid="visits-today-card" onClick={() => {
              const today = new Date().toISOString().split('T')[0];
              navigate(`/site-visits?date_from=${today}&date_to=${today}`);
            }}>
            <CardContent className="p-6 cursor-pointer">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Site Visits Today</p>
                  <p className="text-3xl font-bold text-purple-600 font-['Manrope'] mt-1">{stats?.visits_today || 0}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              {stats?.rescheduled_today > 0 && (
                <p className="text-sm text-amber-600 mt-3 font-medium">{stats.rescheduled_today} rescheduled today</p>
              )}
              {!stats?.rescheduled_today && <p className="text-sm text-slate-500 mt-3">Scheduled for today</p>}
            </CardContent>
          </Card>
        </div>

        {/* Anomaly Alerts */}
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="anomaly-alerts-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-600" />
              <CardTitle className="text-lg font-semibold text-slate-900 font-['Manrope']">AI Anomaly Detection</CardTitle>
              <InfoTip text="AI scans your pipeline weekly for unusual patterns — sudden drops in lead inflow, spikes in lost leads, overdue tasks piling up. Cached for 6 hours; click refresh for a new analysis." />
            </div>
            <div className="flex items-center gap-3">
              {anomalies && (
                <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                  anomalies.health_score >= 70 ? 'bg-emerald-100 text-emerald-700' :
                  anomalies.health_score >= 40 ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }`} data-testid="anomaly-health-score">
                  Health: {anomalies.health_score}/100
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchAnomalies(true)}
                disabled={anomalyLoading}
                data-testid="refresh-anomalies-btn"
              >
                <RefreshCw className={`w-4 h-4 ${anomalyLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {anomalyLoading && !anomalies ? (
              <div className="py-6 text-center text-slate-500">Scanning for anomalies...</div>
            ) : anomalies?.anomalies?.length > 0 ? (
              <div className="space-y-3">
                {anomalies.anomalies.map((a, i) => (
                  <div key={i} className={`flex items-start gap-3 p-3 rounded-lg ${
                    a.type === 'critical' ? 'bg-red-50 border border-red-200' :
                    a.type === 'warning' ? 'bg-amber-50 border border-amber-200' :
                    'bg-blue-50 border border-blue-200'
                  }`} data-testid={`anomaly-item-${i}`}>
                    {a.type === 'critical' ? <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" /> :
                     a.type === 'warning' ? <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" /> :
                     <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 text-sm">{a.title}</p>
                      <p className="text-sm text-slate-600 mt-0.5">{a.description}</p>
                      {a.recommendation && (
                        <p className="text-xs text-slate-500 mt-1 italic">Action: {a.recommendation}</p>
                      )}
                    </div>
                  </div>
                ))}
                {anomalies.trend && (
                  <div className="flex items-center gap-2 pt-2 text-sm text-slate-500">
                    <TrendingUp className="w-4 h-4" />
                    Trend: <span className={`font-medium capitalize ${
                      anomalies.trend === 'improving' ? 'text-emerald-600' :
                      anomalies.trend === 'declining' ? 'text-red-600' : 'text-slate-600'
                    }`}>{anomalies.trend}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-6 text-center text-slate-500">No anomalies detected. Everything looks healthy!</div>
            )}
          </CardContent>
        </Card>

        {/* Second Row - Pipeline & Score Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Lead Scores */}
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-slate-900 font-['Manrope']">Lead Scores</CardTitle>
              <InfoTip text="Hot = high buying intent. Warm = interested. Nurture = early stage. Dormant = inactive. Click any score to see those leads." />
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors" onClick={() => openLeadsTab({ score: "hot", date_from: dateFrom, date_to: dateTo })}>
                  <div className="flex items-center gap-3">
                    <Flame className="w-5 h-5 text-red-600" />
                    <span className="font-medium text-slate-700">Hot</span>
                  </div>
                  <span className="text-xl font-bold text-red-600">{stats?.score_counts?.hot || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors" onClick={() => openLeadsTab({ score: "warm", date_from: dateFrom, date_to: dateTo })}>
                  <div className="flex items-center gap-3">
                    <ThermometerSun className="w-5 h-5 text-amber-600" />
                    <span className="font-medium text-slate-700">Warm</span>
                  </div>
                  <span className="text-xl font-bold text-amber-600">{stats?.score_counts?.warm || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors" onClick={() => openLeadsTab({ score: "nurture", date_from: dateFrom, date_to: dateTo })}>
                  <div className="flex items-center gap-3">
                    <Snowflake className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-slate-700">Nurture</span>
                  </div>
                  <span className="text-xl font-bold text-blue-600">{stats?.score_counts?.nurture || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => openLeadsTab({ score: "dormant", date_from: dateFrom, date_to: dateTo })}>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-slate-500" />
                    <span className="font-medium text-slate-700">Dormant</span>
                  </div>
                  <span className="text-xl font-bold text-slate-500">{stats?.score_counts?.dormant || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lead Sources */}
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-slate-900 font-['Manrope']">Lead Sources</CardTitle>
              <p className="text-xs text-slate-400 mt-0.5">Where your leads are coming from. Click any source to view those leads.</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(stats?.source_counts || {})
                  .sort((a, b) => b[1] - a[1])
                  .map(([source, count]) => {
                    const meta = SOURCE_META[source] || SOURCE_META.other;
                    const label = SOURCES.find(s => s.value === source)?.label
                      || (source ? source.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "—");
                    const { Icon } = meta;
                    return (
                      <div
                        key={source}
                        className="flex items-center justify-between cursor-pointer hover:bg-slate-50 rounded-lg p-1 -mx-1 transition-colors"
                        onClick={() => openLeadsTab({ source, date_from: dateFrom, date_to: dateTo })}
                        data-testid={`source-row-${source}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-slate-50 rounded-lg flex items-center justify-center border border-slate-100">
                            <Icon className={`w-4 h-4 ${meta.iconCls}`} />
                          </div>
                          <span className="text-sm text-slate-700">{label}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${meta.barCls} rounded-full transition-all`}
                              style={{ width: `${Math.max(2, (count / (stats?.total_leads || 1)) * 100)}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-slate-600 w-10 text-right">{count}</span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Leads */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-semibold text-slate-900 font-['Manrope']">Recent Leads</CardTitle>
            <Button 
              variant="ghost" 
              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
              onClick={() => navigate('/leads')}
            >
              View All
            </Button>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-2">Name</th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-2">Source</th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-2">Score</th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-2">Stage</th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-2">Budget</th>
                  </tr>
                </thead>
                <tbody>
                  {recentLeads.map((lead) => (
                    <tr 
                      key={lead.lead_id} 
                      className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/leads/${lead.lead_id}`)}
                      data-testid={`lead-row-${lead.lead_id}`}
                    >
                      <td className="py-3 px-2">
                        <div>
                          <p className="font-medium text-slate-900">{lead.name}</p>
                          <p className="text-sm text-slate-500">{lead.phone}</p>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <span className="text-sm text-slate-600 capitalize">{lead.source?.replace(/_/g, ' ')}</span>
                      </td>
                      <td className="py-3 px-2">
                        <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full capitalize ${getScoreBadge(lead.score)}`}>
                          {lead.score}
                        </span>
                      </td>
                      <td className="py-3 px-2">
                        <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full capitalize ${getStageBadge(lead.stage)}`}>
                          {lead.stage?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-sm text-slate-600">
                        {formatINR(lead.budget_min)} - {formatINR(lead.budget_max)}
                      </td>
                    </tr>
                  ))}
                  {recentLeads.length === 0 && (
                    <tr>
                      <td colSpan="5" className="py-8 text-center text-slate-500">
                        No leads yet. Create your first lead to get started.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Conversion Rate & Stage Drop-off - Auto Flip Widget */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(() => {
            const dropoffStages = (stats?.stage_dropoff || []).filter(s => s.stage !== 'booked');
            const totalSlides = 1 + dropoffStages.length;
            const currentStage = currentDropoffIndex > 0 ? dropoffStages[currentDropoffIndex - 1] : null;
            const stageLabels = {
              new: { from: 'New', to: 'Contacted' },
              contacted: { from: 'Contacted', to: 'Qualified' },
              qualified: { from: 'Qualified', to: 'SV Scheduled' },
              site_visit_scheduled: { from: 'SV Scheduled', to: 'Site Visit' },
              site_visit: { from: 'Site Visit', to: 'Revisit' },
              revisit: { from: 'Revisit', to: 'Nego Scheduled' },
              negotiation_scheduled: { from: 'Nego Scheduled', to: 'Negotiation' },
              negotiation: { from: 'Negotiation', to: 'Booked' },
            };
            
            return (
              <div 
                className="col-span-1 md:col-span-2 relative h-[200px] perspective-1000"
                onMouseEnter={() => clearInterval(flipIntervalRef.current)}
                onMouseLeave={() => {
                  flipIntervalRef.current = setInterval(() => {
                    setCurrentDropoffIndex(prev => {
                      const next = (prev + 1) % totalSlides;
                      setConversionFlipped(next > 0);
                      return next;
                    });
                  }, 4000);
                }}
              >
                <div 
                  className={`absolute inset-0 transition-transform duration-700 transform-style-3d ${conversionFlipped ? 'rotate-y-180' : ''}`}
                  style={{ transformStyle: 'preserve-3d' }}
                >
                  {/* Front - Conversion Rate */}
                  <Card 
                    className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0 cursor-pointer hover:from-slate-800 hover:to-slate-700 transition-colors backface-hidden"
                    style={{ backfaceVisibility: 'hidden' }}
                    onClick={() => openLeadsTab({ stage: "booked", date_from: dateFrom, date_to: dateTo })}
                    data-testid="conversion-rate-card"
                  >
                    <CardContent className="p-6 h-full flex flex-col justify-between">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-slate-400 text-sm">Conversion Rate</p>
                            <InfoTip text="Percentage of leads that reached 'Booked' stage. Auto-cycles through stage drop-offs." dark />
                          </div>
                          <p className="text-4xl font-bold font-['Manrope'] mt-1">{stats?.conversion_rate || 0}%</p>
                          <p className="text-slate-400 text-sm mt-2">Lead to Booking</p>
                        </div>
                        <div className="w-24 h-24 relative">
                          <svg className="w-24 h-24 transform -rotate-90">
                            <circle cx="48" cy="48" r="40" stroke="rgba(255,255,255,0.1)" strokeWidth="8" fill="none" />
                            <circle 
                              cx="48" cy="48" r="40" 
                              stroke="#D97706" 
                              strokeWidth="8" 
                              fill="none" 
                              strokeDasharray={`${(stats?.conversion_rate || 0) * 2.51} 251`}
                              strokeLinecap="round"
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <TrendingUp className="w-8 h-8 text-amber-500" />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>Click for booked leads</span>
                        <button 
                          onClick={(e) => { e.stopPropagation(); setCurrentDropoffIndex(1); setConversionFlipped(true); }}
                          className="flex items-center gap-1 text-amber-400 hover:text-amber-300"
                        >
                          View Drop-offs <ChevronRight className="w-3 h-3" />
                        </button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Back - Single Stage Drop-off */}
                  <Card 
                    className={`absolute inset-0 text-white border-0 transition-colors ${
                      currentStage?.dropoff_rate > 50 
                        ? 'bg-gradient-to-br from-rose-800 to-rose-950' 
                        : currentStage?.dropoff_rate > 25 
                          ? 'bg-gradient-to-br from-amber-700 to-amber-950'
                          : 'bg-gradient-to-br from-emerald-700 to-emerald-950'
                    }`}
                    style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                    data-testid="stage-dropoff-card"
                  >
                    <CardContent className="p-6 h-full flex flex-col justify-between">
                      {currentStage && (
                        <>
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-white/60 text-xs uppercase tracking-wider">Stage Drop-off</p>
                              <div className="flex items-center gap-1">
                                <button 
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    const prev = currentDropoffIndex <= 1 ? dropoffStages.length : currentDropoffIndex - 1;
                                    setCurrentDropoffIndex(prev);
                                  }}
                                  className="p-1 hover:bg-white/10 rounded"
                                >
                                  <ChevronLeft className="w-4 h-4" />
                                </button>
                                <button 
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    const next = currentDropoffIndex >= dropoffStages.length ? 1 : currentDropoffIndex + 1;
                                    setCurrentDropoffIndex(next);
                                  }}
                                  className="p-1 hover:bg-white/10 rounded"
                                >
                                  <ChevronRight className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <p className="text-lg font-medium text-white/90">
                              {stageLabels[currentStage.stage]?.from || currentStage.stage} → {stageLabels[currentStage.stage]?.to || 'Next'}
                            </p>
                            <p className={`text-5xl font-bold font-['Manrope'] mt-2 ${
                              currentStage.dropoff_rate > 50 ? 'text-rose-300' : currentStage.dropoff_rate > 25 ? 'text-amber-300' : 'text-emerald-300'
                            }`}>
                              {currentStage.dropoff_rate}%
                            </p>
                            <p className="text-white/50 text-sm mt-1">leads didn't progress</p>
                          </div>
                          
                          <div>
                            <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden mb-3">
                              <div 
                                className={`h-full rounded-full transition-all duration-500 ${
                                  currentStage.dropoff_rate > 50 ? 'bg-rose-400' : currentStage.dropoff_rate > 25 ? 'bg-amber-400' : 'bg-emerald-400'
                                }`}
                                style={{ width: `${Math.min(currentStage.dropoff_rate, 100)}%` }}
                              />
                            </div>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-white/50">
                                {currentStage.leads_progressed || 0} of {currentStage.leads_entered || 0} progressed, {currentStage.count} stuck here
                              </span>
                              <button 
                                onClick={(e) => { e.stopPropagation(); setCurrentDropoffIndex(0); setConversionFlipped(false); }}
                                className="text-white/60 hover:text-white flex items-center gap-1"
                              >
                                <ChevronLeft className="w-3 h-3" /> Conversion
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </div>
                
                {/* Slide indicator dots */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
                  <button 
                    onClick={() => { setCurrentDropoffIndex(0); setConversionFlipped(false); }}
                    className={`w-2 h-2 rounded-full transition-all ${currentDropoffIndex === 0 ? 'bg-amber-500 w-4' : 'bg-white/30 hover:bg-white/50'}`}
                    title="Conversion Rate"
                  />
                  {dropoffStages.map((stage, idx) => (
                    <button 
                      key={stage.stage}
                      onClick={() => { setCurrentDropoffIndex(idx + 1); setConversionFlipped(true); }}
                      className={`w-2 h-2 rounded-full transition-all ${
                        currentDropoffIndex === idx + 1 
                          ? `${stage.dropoff_rate > 50 ? 'bg-rose-500' : stage.dropoff_rate > 25 ? 'bg-amber-500' : 'bg-emerald-500'} w-4` 
                          : 'bg-white/30 hover:bg-white/50'
                      }`}
                      title={`${stage.stage.replace(/_/g, ' ')} drop-off`}
                    />
                  ))}
                </div>
              </div>
            );
          })()}

          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-6 flex flex-col justify-center h-full">
              <div className="text-center">
                <p className="text-slate-500 text-sm">AI Copilot</p>
                <p className="text-lg font-semibold text-slate-900 mt-2 font-['Manrope']">Get AI-powered insights</p>
                <Button 
                  className="mt-4 bg-amber-500 hover:bg-amber-600 text-white"
                  onClick={() => navigate('/copilot')}
                  data-testid="copilot-btn"
                >
                  Open Copilot
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
